import { Table } from "@tanstack/react-table";

import PaginationComponent from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { DatasetExploreItem } from "@/types/dataset";

import { DatasetDetailRow } from "./details/columns";

interface PaginationProps<TData, TRow> {
  readonly table: Table<TData> | Table<TRow>;
  readonly pageSize: number;
  readonly pageSizeOptions?: number[];
  readonly setPageSize: (pageSize: number) => void;
  readonly currentPage: number;
  readonly setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  readonly totalData: number;
}

export default function Pagination<
  TData = DatasetExploreItem,
  TRow = DatasetDetailRow
>({
  table,
  pageSize,
  pageSizeOptions = [10, 25, 50, 100],
  setPageSize,
  setCurrentPage,
  currentPage,
  totalData
}: PaginationProps<TData, TRow>) {
  const totalPages = table.getPageCount();

  const handlePageChange = (newPageIndex: number) => {
    table.setPagination({
      pageIndex: newPageIndex,
      pageSize
    });
    setCurrentPage(newPageIndex);
  };

  return (
    <div className="flex flex-row items-center mt-auto relative">
      <div className="flex items-center text-txtBody text-sm mr-auto">
        <Select
          value={String(pageSize)}
          onValueChange={(value) => {
            const newPageSize = Number(value);
            table.setPagination({
              pageIndex: 1,
              pageSize: newPageSize
            });
            setCurrentPage(1);
            setPageSize(newPageSize);
          }}
        >
          <SelectTrigger className="w-fit h-8">
            <SelectValue placeholder="10" />
          </SelectTrigger>
          <SelectContent className="z-[9999]" side="top">
            {pageSizeOptions.map((pageSize) => (
              <SelectItem
                aria-label={`Page size ${pageSize}`}
                key={pageSize}
                value={String(pageSize)}
              >
                {pageSize}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="ml-2">of {totalData} entries</span>
      </div>

      <PaginationComponent
        currentPage={currentPage || 1}
        totalPages={totalPages || 0}
        onPageChange={handlePageChange}
      />
    </div>
  );
}
